import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CONSOLIDATION } from "@/constants/testIds";
import {
  MessageCircle,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

export default function GreetingTemplateSection() {
  const [body, setBody] = useState("");
  const [original, setOriginal] = useState("");
  const [defaultBody, setDefaultBody] = useState("");
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/consolidation/greeting-template");
      setBody(data.body || "");
      setOriginal(data.body || "");
      setDefaultBody(data.default || "");
      setConfigured(!!data.configured);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando plantilla");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, []);

  const save = async () => {
    setError("");
    if (body.trim().length < 10) {
      setError("La plantilla debe tener al menos 10 caracteres");
      return;
    }
    setSaving(true);
    try {
      await api.put("/consolidation/greeting-template", { body });
      setOriginal(body);
      setInfo("Plantilla actualizada");
      setTimeout(() => setInfo(""), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setBody(defaultBody);
  };

  if (loading) return null;

  const dirty = body !== original;

  return (
    <section
      data-testid={CONSOLIDATION.templateSection}
      className="mt-10 border border-[#d9cfb8] bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 text-[#1c1a14]">
            <MessageCircle size={16} className="text-[#0f7a4a]" />
            <h3 className="font-serif text-base">Saludo pastoral automático (SMS)</h3>
          </div>
          <p className="text-[11px] text-[#5d5849] mt-0.5">
            Mensaje que se envía automáticamente al registrar un visitante con teléfono.
            Usa <code className="bg-[#fbf7ee] px-1">{"{nombre}"}</code> y{" "}
            <code className="bg-[#fbf7ee] px-1">{"{iglesia}"}</code> como variables.
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
            configured
              ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
              : "bg-amber-100 text-amber-800 border border-amber-300"
          }`}
        >
          {configured ? "Twilio activo" : "Modo simulación"}
        </span>
      </div>

      {!configured && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <strong>Modo simulación.</strong> Para activar envíos reales, configure las
          variables <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code> y{" "}
          <code>TWILIO_PHONE_FROM</code> en <code>/app/backend/.env</code> y reinicie el
          backend.
        </div>
      )}

      <textarea
        data-testid={CONSOLIDATION.templateInput}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={7}
        className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded font-mono focus:border-[#c8a04a] focus:outline-none whitespace-pre-wrap"
      />
      <div className="mt-1 text-[10px] text-[#8b7e5e]">
        {body.length} / 1600 caracteres
      </div>

      {error && (
        <div className="mt-2 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {info && !error && (
        <div
          data-testid={CONSOLIDATION.templateInfoMsg}
          className="mt-2 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700"
        >
          <CheckCircle2 size={14} /> {info}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          data-testid={CONSOLIDATION.templateResetButton}
          onClick={resetToDefault}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#5b5240] hover:text-[#1c1a14]"
        >
          <RotateCcw size={12} /> Restaurar predeterminado
        </button>
        <button
          type="button"
          data-testid={CONSOLIDATION.templateSaveButton}
          onClick={save}
          disabled={!dirty || saving}
          className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Guardando..." : "Guardar plantilla"}
        </button>
      </div>
    </section>
  );
}
